import * as React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import LicensePlate from './LicensePlate';

// Orders in these statuses are over: no live indicator, no time, no calling the driver
const FINISHED_STATUSES = [80, 90];

type TimeInfo = {
  minutes: number,
  caption: 'time_remaining' | 'time_trip_estimate' | 'time_on_trip',
};

const minutesBetween = (from: number, to: number) => Math.max(0, Math.floor((to - from) / 60000));

const PhoneIcon = (): JSX.Element => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path
      fill="currentColor"
      d={[
        'M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1',
        'A17 17 0 0 1 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.3.2 2.5.6 3.6.1.3 0 .7-.2 1l-2.3 2.2Z',
      ].join('')}
    />
  </svg>
);

const StarIcon = (): JSX.Element => (
  <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
    <path
      fill="currentColor"
      d="m12 17.3 6.2 3.7-1.6-7 5.4-4.7-7.1-.6L12 2 9.1 8.7 2 9.3l5.4 4.7-1.6 7z"
    />
  </svg>
);

const DriverAvatar = ({ name, picture }: { name: string, picture: string | null }): JSX.Element => {
  const [failed, setFailed] = React.useState(false);

  if (picture && !failed) {
    return (
      <img
        className="driver__avatar"
        src={picture}
        alt=""
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span className="driver__avatar driver__avatar--initial" aria-hidden="true">
      {name.charAt(0).toUpperCase()}
    </span>
  );
};

const FirstSlide = (): JSX.Element => {
  const intl = useIntl();
  const { order: orderState } = useSelector((state: CR.RootState) => state.order);
  const order = orderState as CR.OrderCRUD;

  const { routes } = useSelector((state: CR.RootState) => state.graphHopper);

  const isFinished = FINISHED_STATUSES.includes(order.status?.id);
  const { driver, car } = order;

  let time: TimeInfo | undefined;

  if (!isFinished) {
    if (routes[0] !== undefined) {
      // With tracking the route starts at the driver; without it, it spans pickup to destination
      const hasTracking = !!order.tracking && order.tracking.length > 0;
      time = {
        minutes: Math.floor((routes[0].summary?.totalTime as number) / 60000),
        caption: hasTracking ? 'time_remaining' : 'time_trip_estimate',
      };
    } else if (order.route_points.length === 1 && order.route_points[0].ended_at) {
      // No destination set: show how long the passenger has been on board
      time = {
        minutes: minutesBetween(Date.parse(`${order.route_points[0].ended_at}Z`), Date.now()),
        caption: 'time_on_trip',
      };
    }
  }

  return (
    <div className="summary">
      <div className="summary__status">
        <div className="status">
          {!isFinished && <span className="status__live" aria-hidden="true" />}
          <h1 className="status__name">{order.status?.name}</h1>
        </div>

        {time && (
          <div className="eta">
            <span className="eta__value">
              {time.minutes}
              <span className="eta__unit"> min</span>
            </span>
            <span className="eta__caption">
              <FormattedMessage id={time.caption} />
            </span>
          </div>
        )}
      </div>

      {car && (
        <div className="vehicle">
          <LicensePlate number={car.registration_nb} />

          <div className="vehicle__meta">
            <span className="vehicle__model">
              {`${car.brand.name} ${car.model.name}`}
            </span>
            <span className="vehicle__color">
              {car.color.color && (
                <span className="vehicle__swatch" style={{ backgroundColor: car.color.color }} />
              )}
              {car.color.name}
            </span>
          </div>
        </div>
      )}

      {driver && (
        <div className="driver">
          <DriverAvatar name={driver.first_name} picture={driver.profile_picture} />

          <div className="driver__info">
            <span className="driver__name">{driver.first_name}</span>
            {driver.rating !== null && driver.rating !== undefined && (
              <span className="driver__rating">
                <StarIcon />
                {driver.rating.toFixed(1)}
              </span>
            )}
          </div>

          {driver.phone && !isFinished && (
            <a
              className="callButton"
              href={`tel:${driver.phone}`}
              aria-label={intl.formatMessage({ id: 'call_driver_label' }, { name: driver.first_name })}
            >
              <PhoneIcon />
              <FormattedMessage id="call_driver" />
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default FirstSlide;
